import { StandardEvents } from '@shopify/events';

export function productTypes(cards) {
  return [...new Set(cards.map(card => card.dataset.productType).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }) || a.localeCompare(b));
}
export function pageCards(cards, type, page, size = 24) {
  const matches = cards.filter(card => !type || card.dataset.productType === type);
  return { matches, visible: matches, pages: 1, current: 1 };
}
class EssentialsCollection extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
    this.grid = this.querySelector('[data-cards]');
    if (!this.grid) return;
    this.querySelector('[data-native-pages]').hidden = true;
    this.querySelector('[data-retry]').addEventListener('click', () => this.loadCatalogue(), { signal: this.controller.signal });
    this.querySelector('[data-prev]').addEventListener('click', () => this.showPage(this.page - 1), { signal: this.controller.signal });
    this.querySelector('[data-next]').addEventListener('click', () => this.showPage(this.page + 1), { signal: this.controller.signal });
    this.querySelector('[data-types]').addEventListener('click', event => { const button = event.target.closest('button[data-type]'); if (button) { this.type = button.dataset.type; this.showPage(1); } }, { signal: this.controller.signal });
    if (this.complete) this.showPage(this.page || 1); else this.loadCatalogue();
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  pageURL(value) {
    const url = new URL(value, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) throw new Error('Unexpected pagination route');
    url.searchParams.set('view', 'size24-essentials');
    url.searchParams.set('section_id', this.dataset.sectionId);
    return url;
  }
  async loadCatalogue() {
    const controller = this.controller;
    if (this.loading === controller) return;
    this.loading = controller;
    const status = this.querySelector('[data-loading]');
    this.loadingText ||= status.textContent;
    status.textContent = this.loadingText; status.hidden = false;
    this.querySelector('[data-retry]').hidden = true;
    try {
      const expected = Number(this.dataset.total);
      if (!Number.isSafeInteger(expected) || expected < 0) throw new Error('Invalid catalogue count');
      let scope = this;
      const initialPage = Number(this.querySelector('[data-catalogue]').dataset.page);
      const startsOnFirstPage = initialPage === 1;
      const initialCards = new Map([...this.grid.querySelectorAll('[data-essential-card]')].map(card => [card.dataset.productId, card]));
      let url = null;
      if (!startsOnFirstPage) { url = this.pageURL(window.location.href); url.searchParams.set('page', '1'); }
      const cards = [], ids = new Set(), visited = new Set();
      while (true) {
        if (url) {
          if (visited.has(url.href)) throw new Error('Repeated pagination route');
          visited.add(url.href);
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Catalogue page unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          scope = document.querySelector('s24-essentials');
          if (!scope || scope.dataset.collectionId !== this.dataset.collectionId || Number(scope.dataset.total) !== expected) throw new Error('Catalogue changed; retry');
        }
        const batch = [...scope.querySelectorAll('[data-essential-card]')];
        if (!batch.length && expected > cards.length) throw new Error('Incomplete catalogue');
        for (const card of batch) {
          const id = card.dataset.productId;
          if (!id || ids.has(id)) throw new Error('Repeated catalogue product');
          ids.add(id); cards.push(card);
        }
        if (cards.length > expected) throw new Error('Catalogue count changed');
        const next = scope.querySelector('[data-next-page]');
        if (!next) break;
        url = this.pageURL(next.getAttribute('href'));
      }
      if (cards.length !== expected) throw new Error('Incomplete catalogue');
      if (this.controller !== controller || !this.isConnected) return;
      if ([...initialCards.keys()].some(id => !ids.has(id))) throw new Error('Catalogue changed; retry');
      let anchor = this.grid.firstElementChild;
      for (let index = 0; index < cards.length; index++) {
        const live = initialCards.get(cards[index].dataset.productId);
        if (live) { cards[index] = live; anchor = live.nextElementSibling; }
        else this.grid.insertBefore(cards[index], anchor);
      }
      this.cards = cards; this.complete = true; this.type = ''; this.page = initialPage;
      const types = this.querySelector('[data-types]'); types.replaceChildren();
      for (const type of ['', ...productTypes(cards)]) {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = type || this.messages.all; button.dataset.type = type;
        types.append(button);
      }
      this.querySelector('[data-filter-wrap]').hidden = false;
      this.querySelector('[data-native-pages]').hidden = true;
      status.hidden = true;
      this.showPage(this.page);
    } catch (error) {
      if (controller.signal.aborted || this.controller !== controller) return;
      status.textContent = this.messages.load_error;
      this.querySelector('[data-native-pages]').hidden = false;
      this.querySelector('[data-retry]').hidden = false;
    } finally {
      if (this.loading === controller) this.loading = null;
    }
  }
  showPage(page) {
    const result = pageCards(this.cards, this.type, page); this.page = result.current;
    const visible = new Set(result.visible); for (const card of this.cards) card.hidden = !visible.has(card);
    for (const button of this.querySelectorAll('[data-type]')) button.setAttribute('aria-pressed', String(button.dataset.type === this.type));
    this.querySelector('[data-result-count]').textContent = this.messages.count.replace('__COUNT__', String(result.matches.length));
    this.querySelector('[data-page-status]').textContent = this.messages.page.replace('__CURRENT__', String(result.current)).replace('__TOTAL__', String(result.pages));
    this.querySelector('[data-client-pages]').hidden = result.pages < 2;
    this.querySelector('[data-prev]').disabled = result.current === 1;
    this.querySelector('[data-next]').disabled = result.current === result.pages;
  }
}
class EssentialsOrder extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.form = this.querySelector('form'); this.button = this.querySelector('[type="submit"]'); this.status = this.querySelector('[data-cart-status]');
    this.messages = JSON.parse(this.querySelector('[data-order-messages]').textContent);
    this.syncBusy();
    this.addEventListener('submit', event => {
      const id = this.form.querySelector('[name="id"]');
      if (this.busy || this.dataset.available !== 'true' || id.disabled || !id.value || !this.form.checkValidity()) { event.preventDefault(); event.stopImmediatePropagation(); this.form.reportValidity(); }
    }, { capture: true, signal: this.controller.signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (event.target.closest('s24-essentials-order') !== this || !event.promise) return;
      this.busy = true; this.status.textContent = this.messages.adding; this.syncBusy();
      event.promise.then(result => { this.finish(result.detail?.didError ? this.messages.error : this.messages.added); }).catch(() => this.finish(this.messages.error));
    }, { signal: this.controller.signal });
  }
  syncBusy() { this.button.disabled = Boolean(this.busy) || this.dataset.available !== 'true'; this.form.setAttribute('aria-busy', String(Boolean(this.busy))); }
  finish(message) { this.busy = false; if (this.isConnected) { this.syncBusy(); this.status.textContent = message; } }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('s24-essentials')) customElements.define('s24-essentials', EssentialsCollection);
if (!customElements.get('s24-essentials-order')) customElements.define('s24-essentials-order', EssentialsOrder);
